import { message, Space } from "antd";
import { type BTFileSystem } from "legacyComponents/FileUploadContainer.types";
import { useCallback, useState } from "react";

import { MediaType } from "types/enum";

import { getBase64ImageFromUrl, validateBase64Image } from "utilities/file/file";
import { useActionBeingPerformed } from "utilities/form/form";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import {
    BTConfirm,
    BTConfirmContent,
    BTConfirmFooter,
} from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { BTForm, BTFormItem } from "commonComponents/btWrappers/BTForm/BTForm";
import { supportedEditorImageTypes } from "commonComponents/btWrappers/editor/core/editor.utils";
import ImageExtension from "commonComponents/btWrappers/editor/core/extensions/custom/image/ImageExtension";
import { findExtension } from "commonComponents/btWrappers/editor/core/extensions/editorExtensionUtils";
import { type IEditor } from "commonComponents/btWrappers/editor/core/useBTEditor.types";
import { BrowseBTFilesDropdown } from "commonComponents/entity/media/BrowseBTFiles/BrowseBTFilesDropdown";

import "./BrowseImagesOverlay.less";

interface IBrowseImagesOverlayProps {
    editor: IEditor;
}

type BrowseImagesOverlayActions = "insert" | undefined;

export function BrowseImagesOverlay({ editor }: IBrowseImagesOverlayProps) {
    const [selectedFile, setSelectedFile] = useState<BTFileSystem | undefined>(undefined);
    const [actionBeingPerformed, setActionBeingPerformed] =
        useActionBeingPerformed<BrowseImagesOverlayActions>();

    const handleInsert = useCallback(async () => {
        await setActionBeingPerformed({
            actionBeingPerformed: "insert",
            callback: async () => {
                if (selectedFile) {
                    try {
                        const base64String = await getBase64ImageFromUrl(selectedFile.docPath);
                        await validateBase64Image(base64String);
                        editor.tiptapEditor
                            .chain()
                            .focus()
                            .setImage({
                                src: base64String,
                            })
                            .run();
                        editor.editorStateDispatch({
                            type: "setShowBrowseImagesOverlay",
                            payload: false,
                        });
                    } catch (e) {
                        console.error(e);
                        void message.error(
                            "Image could not be inserted. Please try again or contact support.",
                        );
                    }
                }
            },
        });
    }, [selectedFile, editor, setActionBeingPerformed]);

    if (!editor.editorState.showImageUpload) {
        return null;
    }

    const browseFilesJobId = findExtension(editor.tiptapEditor, ImageExtension)?.options
        ?.browseBuildertrendFilesConfig?.jobId;

    if (browseFilesJobId === undefined) {
        throw new Error(
            "Browse buildertrend images overlay was loaded, but a valid configuration was not provided",
        );
    }

    return (
        <BTConfirm visible fitToContent title="Insert image from Buildertrend files">
            <BTForm>
                <BTConfirmContent>
                    <div className="BrowseImagesOverlay--UploadContainer">
                        <BTFormItem>
                            <BrowseBTFilesDropdown
                                id="browseFilePicker"
                                allowedMediaTypes={[MediaType.Photo]}
                                onBlur={() => {}}
                                onFileSelected={async (file) => {
                                    setSelectedFile(file);
                                }}
                                data-testid="browseFilePicker"
                                allowedFileTypes={Array.from(supportedEditorImageTypes.extensions)}
                                jobId={browseFilesJobId}
                            />
                        </BTFormItem>
                    </div>
                </BTConfirmContent>
                <BTConfirmFooter>
                    <Space size="small">
                        <BTButtonDeprecated<BrowseImagesOverlayActions>
                            data-testid="cancelInsertImageToEditor"
                            onClick={() =>
                                editor.editorStateDispatch({
                                    type: "setShowBrowseImagesOverlay",
                                    payload: false,
                                })
                            }
                            actionBeingPerformed={actionBeingPerformed}
                        >
                            Cancel
                        </BTButtonDeprecated>
                        <BTButtonDeprecated<BrowseImagesOverlayActions>
                            data-testid="insertImageToEditor"
                            type="primary"
                            onClick={handleInsert}
                            disabled={!selectedFile}
                            loadingAction="insert"
                            actionBeingPerformed={actionBeingPerformed}
                        >
                            Insert Image
                        </BTButtonDeprecated>
                    </Space>
                </BTConfirmFooter>
            </BTForm>
        </BTConfirm>
    );
}
